import { useEffect, useCallback, createContext, useState } from "react";
import { getCurrentUser, fetchUserAttributes } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";

export interface User {
  sessionExpired: boolean;
  isLoggedIn: boolean;
  name?: string;
  userRole: string | undefined;
}

interface AuthContextType {
  loading: boolean;
  auth: User;
  logout: () => void;
  expiresSession: () => void;
}

export const AuthContext = createContext<AuthContextType>({
  loading: false,
  auth: {
    sessionExpired: false,
    isLoggedIn: false,
    name: undefined,
    userRole: undefined,
  },
  logout: () => {},
  expiresSession: () => {},
});

export const useAuthentication = (): AuthContextType => {
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<User>({
    sessionExpired: false,
    isLoggedIn: false,
    name: undefined,
    userRole: undefined,
  });

  const login = useCallback(
    (attrs: { name?: string; "custom:user_role"?: string }) => {
      setLoading(false);
      setUser(() => ({
        sessionExpired: false,
        isLoggedIn: true,
        name: attrs.name,
        userRole: attrs["custom:user_role"],
      }));
    },
    [setUser],
  );

  const logout = useCallback(() => {
    setUser(() => ({
      isLoggedIn: false,
      sessionExpired: false,
      name: undefined,
      userRole: undefined,
    }));
  }, [setUser]);

  const expiresSession = useCallback(() => {
    setUser((prev) => ({
      ...prev,
      sessionExpired: true,
    }));
  }, [setUser]);

  const currentUser = useCallback(() => {
    if (user.isLoggedIn) {
      return;
    }
    setLoading(true);
    getCurrentUser()
      .then(() => fetchUserAttributes())
      .then((attrs) => {
        login(attrs);
      })
      .catch(() => {
        // 未認証の場合は何もしない
      })
      .finally(() => {
        setLoading(false);
      });
  }, [login, user]);

  useEffect(() => {
    currentUser();
  }, [currentUser]);

  useEffect(() => {
    const unsubscribe = Hub.listen("auth", ({ payload }) => {
      switch (payload.event) {
        case "signedIn":
          currentUser();
          break;
        case "signedOut":
          logout();
          break;
        case "customOAuthState": {
          const data = payload.data;
          if (!data) {
            break;
          }
          const customState = JSON.parse(decodeURIComponent(data));
          if (customState.redirectTo) {
            location.replace(customState.redirectTo);
          }
          break;
        }
      }
    });

    return () => {
      unsubscribe();
    };
  });

  return {
    loading,
    auth: user,
    logout,
    expiresSession,
  };
};
